import React, { useState, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SidebarNavItem from "../components/SidebarNavItem";
import DateFilter from "../components/DateFilter";
import { useDateScroll } from "../contexts/DateScrollContext";
import { useSheets } from "../hooks/useSheets";

function MainLayout({ user, onLogout, loading, authLoading, canViewDebt, onRefresh, rows = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { scrollToDateFn, activeDate } = useDateScroll();
  const { sheets, activeSheet, loading: sheetsLoading, switchSheet, canSwitchMonth } = useSheets(user);

  const path = location.pathname;
  const active = path.includes("/revenue/manage")
    ? "manage"
    : path.includes("/revenue/debt")
    ? "debt"
    : "revenue";

  // Tính uniqueDates từ rows theo thứ tự xuất hiện trong bảng
  const uniqueDates = useMemo(() => {
    const dates = [];
    const seen = new Set();
    rows.forEach((row) => {
      if (row.ngay && row.ngay.trim()) {
        const dateStr = row.ngay.trim();
        if (!seen.has(dateStr)) {
          seen.add(dateStr);
          dates.push(dateStr);
        }
      }
    });
    return dates; // Giữ nguyên thứ tự xuất hiện trong bảng
  }, [rows]);

  // Chỉ hiển thị DateFilter ở trang revenue
  const showDateFilter = active === "revenue" && uniqueDates.length > 0;

  const handleDateClick = (date) => {
    if (scrollToDateFn) {
      scrollToDateFn(date);
    }
  };

  const handleSheetChange = async (e) => {
    const sheetId = parseInt(e.target.value, 10);
    if (!sheetId || isNaN(sheetId)) return;

    try {
      await switchSheet(sheetId);
      // Reload dữ liệu sau khi chuyển đổi tháng để Dashboard cập nhật
      if (onRefresh) {
        // Đợi một chút để đảm bảo server đã cập nhật is_active
        setTimeout(() => {
          onRefresh();
        }, 100);
      }
    } catch (err) {
      alert(err.message || "Không thể chuyển đổi tháng");
    }
  };

  return (
    <div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
      {(loading || authLoading) && (
        <div className="fixed top-0 left-0 right-0 h-0.5 bg-blue-200 z-50">
          <div className="h-full w-full bg-blue-500" />
        </div>
      )}

      {/* Mobile top bar */}
      <div className="md:hidden bg-white border-b border-slate-200 p-3 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Xin chào, <span className="font-semibold text-slate-900">{user?.name}</span>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            mobileOpen ? "block" : "hidden"
          } md:block w-full bg-white border-r border-slate-200 fixed md:sticky md:top-0 inset-y-0 left-0 z-40 md:z-auto h-screen md:h-full overflow-y-auto md:overflow-y-hidden ${
            collapsed ? "md:w-16" : "md:w-[200px]"
          }`}
        >
          <div className="md:hidden p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <span className="text-sm font-semibold text-slate-900">Menu</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-md text-slate-600 hover:bg-slate-100"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-3 sm:p-4 border-b border-slate-100">
            <div className="flex items-start justify-between gap-2">
              <div className={`${collapsed ? "md:hidden" : ""}`}>
                <div className="text-xs sm:text-sm text-slate-600">
                  Xin chào, <span className="font-semibold text-slate-900">{user?.name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Vai trò:{" "}
                  {user?.role?.toLowerCase() === "dispatchers"
                    ? "Điều phối"
                    : user?.role?.toLowerCase() === "accounting"
                    ? "Kế toán"
                    : "Quản trị viên"}
                </div>
              </div>

              {/* Desktop collapse toggle */}
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                className="hidden md:inline-flex items-center justify-center rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                aria-label={collapsed ? "Mở rộng sidebar" : "Thu gọn sidebar"}
                title={collapsed ? "Mở rộng" : "Thu gọn"}
              >
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  {collapsed ? (
                    <path d="M7.5 3.5a1 1 0 0 1 1.414 0l5.086 5.086a2 2 0 0 1 0 2.828L8.914 16.5A1 1 0 1 1 7.5 15.086L12.086 10.5a.5.5 0 0 0 0-.707L7.5 5.207a1 1 0 0 1 0-1.414Z" />
                  ) : (
                    <path d="M12.5 3.5a1 1 0 0 1 0 1.414L7.914 9.5a.5.5 0 0 0 0 .707l4.586 4.586A1 1 0 1 1 11.086 16.5L6 11.414a2 2 0 0 1 0-2.828l5.086-5.086A1 1 0 0 1 12.5 3.5Z" />
                  )}
                </svg>
              </button>
            </div>

            <button
              onClick={onLogout}
              className={`mt-3 inline-flex items-center justify-center rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 ${
                collapsed ? "w-full md:w-full md:px-0 md:py-2" : "w-full md:w-auto"
              }`}
              title={collapsed ? "Đăng xuất" : undefined}
              aria-label={collapsed ? "Đăng xuất" : undefined}
            >
              {collapsed ? (
                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M3 4.5A2.5 2.5 0 0 1 5.5 2h4A2.5 2.5 0 0 1 12 4.5V6a1 1 0 1 1-2 0V4.5a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5V14a1 1 0 1 1 2 0v1.5A2.5 2.5 0 0 1 9.5 18h-4A2.5 2.5 0 0 1 3 15.5v-11Z" />
                  <path d="M13.293 7.293a1 1 0 0 1 1.414 0l2 2a1 1 0 0 1 0 1.414l-2 2a1 1 0 0 1-1.414-1.414L13.586 11H8a1 1 0 1 1 0-2h5.586l-.293-.293a1 1 0 0 1 0-1.414Z" />
                </svg>
              ) : (
                "Đăng xuất"
              )}
            </button>
          </div>

          {/* Select tháng - chỉ hiển thị cho kế toán/điều phối/admin */}
          {canSwitchMonth && sheets.length > 0 && (
            <div className={`p-3 sm:p-4 border-b border-slate-100 ${collapsed ? "md:px-2" : ""}`}>
              {!collapsed && (
                <label className="block text-xs font-medium text-slate-700 mb-2">
                  Chọn tháng
                </label>
              )}
              <select
                value={activeSheet?.id || ""}
                onChange={handleSheetChange}
                disabled={sheetsLoading}
                className={`w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  collapsed ? "md:px-1 md:py-1" : ""
                }`}
                title={collapsed ? activeSheet?.name : undefined}
              >
                {sheets.map((sheet) => (
                  <option key={sheet.id} value={sheet.id}>
                    {sheet.name} {sheet.is_locked ? "🔒" : ""} {sheet.is_default ? "⭐" : ""}
                  </option>
                ))}
              </select>
              {activeSheet?.is_locked && !collapsed ? (
                <p className="mt-1 text-xs text-amber-600">
                  ⚠️ Không thể cập nhật khi tháng bị khóa
                </p>
              ) : ""}
            </div>
          )}

          <div className={`p-3 sm:p-4 space-y-2 ${collapsed ? "md:px-2" : ""}`}>
            <SidebarNavItem
              active={active === "revenue"}
              onClick={() => {
                navigate("/revenue");
                onRefresh?.();
                setMobileOpen(false);
              }}
              title="Điều phối doanh thu xe"
              subtitle="Trang điều phối & đối chiếu"
              collapsed={collapsed}
              icon={
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm1 4a1 1 0 1 0-2 0v4.382l-2.447 1.224a1 1 0 1 0 .894 1.788l3-1.5A1 1 0 0 0 11 11V6Z" />
                </svg>
              }
            />
            <SidebarNavItem
              active={active === "manage"}
              onClick={() => {
                navigate("/revenue/manage");
                setMobileOpen(false);
              }}
              title="Quản lý"
              subtitle="Lái xe • Số xe • Điều phối"
              collapsed={collapsed}
              icon={
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M7 2a1 1 0 0 0-1 1v1H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3h-1V3a1 1 0 1 0-2 0v1H8V3a1 1 0 0 0-1-1Zm8 7H5a1 1 0 1 0 0 2h10a1 1 0 1 0 0-2Z" />
                </svg>
              }
            />
            {canViewDebt && (
              <SidebarNavItem
                active={active === "debt"}
                onClick={() => {
                  navigate("/revenue/debt");
                  onRefresh?.();
                  setMobileOpen(false);
                }}
                title="Công nợ theo xe"
                subtitle="Kế toán xem"
                collapsed={collapsed}
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M2 6.5A2.5 2.5 0 0 1 4.5 4h11A2.5 2.5 0 0 1 18 6.5v7A2.5 2.5 0 0 1 15.5 16h-11A2.5 2.5 0 0 1 2 13.5v-7ZM5 7a1 1 0 0 0 0 2h2a1 1 0 1 0 0-2H5Zm0 4a1 1 0 1 0 0 2h5a1 1 0 1 0 0-2H5Zm10-1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                  </svg>
                }
              />
            )}
          </div>
        </aside>

        {/* Date Filter - cùng cấp với sidebar */}
        {showDateFilter && (
          <aside className={`hidden md:block ${uniqueDates.length > 10 ? 'w-full md:w-20' : 'w-full md:w-12'} bg-white border-r border-slate-200 fixed md:sticky md:top-0 inset-y-0 z-40 md:z-auto h-screen md:h-full overflow-y-auto md:overflow-y-hidden`}>
            <DateFilter
              uniqueDates={uniqueDates}
              onDateClick={handleDateClick}
              activeDate={activeDate}
              className="h-full"
            />
          </aside>
        )}

        <main className="flex-1 overflow-hidden md:ml-0">
          <div className="h-full overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default MainLayout;

